import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UnboaredGame } from '@unboared/react';

import {
    DEFAULT_CLIENT,
    ONBOARDING_FINISHED_ACTION,
    ONBOARDING_GAME_ID,
    ONBOARDING_GAME_ORIGIN,
} from '~/config/global.config';
import { logEvent } from '~/config/firebase.config';
import { GlobalPopup } from '~/components/design/popup/GlobalPopup';
import { ControlPanel } from '~/components/ControlPanel';
import { Mode, ClientDatabase } from '~/types/client';

import {
    ScreenSessionRecovery,
    ScreenStaleLinkReconnect,
    ScreenHeartbeat,
    AnimatorScreenControls,
    ScreenConnectLoader,
} from '~/components/session';
import { normalizeScreenID } from '~/utils/screenID';
import { useTick } from '~/hooks/useTick';
import { usePauseActive } from '~/hooks/usePauseActive';
import { useScreenTabLock } from '~/hooks/useScreenTabLock';
import { ScreenTabChoice } from '~/components/screenTab';
import { useRemoteLaunch } from '~/stores/useRemoteLaunch';
import { useRemoteGameParams } from '~/stores/useRemoteGameParams';
import { useGameParamsFromGame } from '~/stores/useGameParamsFromGame';
import {
    changedParams,
    isMemorizedGame,
    mergeTvParams,
    PUBLIC_DEMO_CLIENTS,
    savedGameParams,
} from '~/config/gameParamsMemory';
import { GAME_PARAMS_SCHEMAS } from '~/config/gameParamsSchema.config';
import { useRemoteStop } from '~/stores/useRemoteStop';
import { useRemoteGameCommand } from '~/stores/useRemoteGameCommand';
import { syncPublicVenueName } from '~/services/playerConsent/publicVenueName';
import { markLeftGame } from '~/utils/menuSession';

import { SeasonalThemeHost } from '~/components/seasonal';
import { BrandCorner } from '~/components/branding/BrandCorner';
import { useBrandTheme, useClientBranding } from '~/hooks/useClientBranding';
import { PublicStage } from '~/components/dualScreen/PublicStage';
import { QRCodeButton } from '~/components/design/buttons/QRCodeButton';
import { PUBLIC_DISPLAY_PARAM, PUBLIC_DISPLAY_VALUE } from '~/services/dualScreen/dualScreen';
import { makeUnboaredConfig } from './config/makeUnboaredConfig';
import { ScreenManager } from '../pages/ScreenManager';
import { LoadingPage } from '../pages/LoadingPage';
import { useSyncClient } from '../hooks/models/useClient';
import { useVenueTitle } from '../hooks/useVenueTitle';

/** Fenêtre de présence animateur : au-delà, l'appli est considérée partie et les
    boutons opérateur de la TV réapparaissent (plan B). ~2 battements de 15s. */
const ANIMATOR_PRESENCE_WINDOW_MS = 30 * 1000;

/**
 * Fin du tutoriel d'onboarding : le jeu unboarding poste
 * { action: 'unboardingFinished' } quand l'utilisateur clique
 * « Découvrir mon espace Unboared » → flag Firestore + retour home.
 */
function useOnboardingFinishedListener(clientID: string, gameID: string) {
    const navigate = useNavigate();

    useEffect(() => {
        if (gameID !== ONBOARDING_GAME_ID) return undefined;

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== ONBOARDING_GAME_ORIGIN) return;
            if (event.data?.action !== ONBOARDING_FINISHED_ACTION) return;
            // Mesure d'impact : terminé vs passé (raison envoyée par le jeu).
            const reason = event.data?.reason === 'skipped' ? 'skipped' : 'completed';
            logEvent(`onboarding_${reason}`, clientID);
            ClientDatabase.completeOnboarding(clientID).catch(() => {
                /* le flag n'est pas bloquant pour continuer */
            });
            navigate('/home');
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [gameID, clientID]);
}

/**
 * Animator Control (pilotage à distance) : l'animateur change de jeu depuis son
 * téléphone (/animator) → ClientDatabase.changeGame écrit screenInfos.anim.gameID.
 * Le client est synchronisé en live (useSyncClient) ; quand le gameID distant
 * CHANGE, l'écran suit en mettant à jour le param `game` de l'URL.
 *
 * Guard : on ne suit ce flag que sur les écrans qui utilisent la clé partagée
 * 'anim' — soit la MÊME condition que le heartbeat ci-dessous
 * (`normalizeScreenID(screenID) === Mode.Anim`), qui couvre anim/bar ET demo, et
 * exclut auto/aéroport (screenID = instance physique).
 *
 * Deux pièges traités :
 * 1. PERSISTANCE PÉRIMÉE + CHARGEMENT ASYNCHRONE. Au montage, le client se charge
 *    de façon asynchrone (useSyncClient renvoie undefined puis la donnée), et
 *    screenInfos.anim.gameID peut être périmé (session précédente — jamais remis à
 *    zéro car stopScreenSession est désactivé). Comparer à la « valeur initiale »
 *    ne suffit donc pas (l'initiale est `undefined`, puis le périmé arrive et
 *    passe pour un changement). On n'active le suivi qu'APRÈS un premier point de
 *    synchro : quand la base reflète enfin le jeu qu'on a nous-même lancé
 *    (remote === url). Tant que ce n'est pas le cas, on ignore le gameID distant
 *    → lancer un jeu différent du précédent ne « revient » plus sur l'ancien.
 *    Ensuite, toute divergence = ordre réel de l'animateur, qu'on applique.
 * 2. On modifie le seul param `game` via setSearchParams pour PRÉSERVER les autres
 *    (notamment `srv`, le token serveur socket : le perdre reconnecterait l'écran
 *    au mauvais serveur et casserait la room — cf bug churn #1).
 */
function useRemoteGameSwitch(client: ReturnType<typeof useSyncClient>, device: string, urlGameID: string) {
    const [, setSearchParams] = useSearchParams();
    const setRef = useRef(setSearchParams);
    setRef.current = setSearchParams;

    const isAnimScreen = !!client && normalizeScreenID(client.getScreenForDevice(device)) === Mode.Anim;
    const remoteGameID = isAnimScreen ? client.getScreen(Mode.Anim)?.gameID : undefined;

    // Devient true au 1er instant où la base reflète le jeu courant de l'écran.
    const syncedRef = useRef(false);

    useEffect(() => {
        if (!isAnimScreen) return;
        if (remoteGameID === urlGameID) {
            // La base a rattrapé notre jeu courant → à partir d'ici, on suit.
            syncedRef.current = true;
            return;
        }
        if (syncedRef.current && remoteGameID) {
            setRef.current(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('game', remoteGameID);
                    return next;
                },
                { replace: true }
            );
        }
    }, [remoteGameID, urlGameID, isAnimScreen]);
}

/**
 * Animator Control (Phase 2A) — lancer la partie à distance. L'animateur appuie
 * sur « Lancer » → ClientDatabase.requestLaunch écrit un nonce horodaté dans
 * screenInfos.anim.launchRequest. On le lit en live et on pousse chaque NOUVEL
 * appui dans le store useRemoteLaunch ; le bridge de l'iframe (useWebViewPlugin)
 * le relaie alors en un onMessage('launch') que le jeu interprète avec SA propre
 * fonction de démarrage.
 *
 * Garde anti-relance fantôme : la valeur présente au montage (potentiellement
 * périmée d'une session précédente — launchRequest n'est jamais remis à zéro) est
 * mémorisée et ignorée ; seuls les changements ultérieurs déclenchent un lancement.
 * Même condition d'écran (clé partagée 'anim') que useRemoteGameSwitch.
 */
function useRemoteLaunchSignal(client: ReturnType<typeof useSyncClient>, device: string) {
    const isAnimScreen = !!client && normalizeScreenID(client.getScreenForDevice(device)) === Mode.Anim;
    const launchReq = isAnimScreen ? client.getScreen(Mode.Anim)?.launchRequest : undefined;

    const seenRef = useRef<number | undefined>(undefined);
    const initRef = useRef(false);

    useEffect(() => {
        if (!isAnimScreen) return;
        if (!initRef.current) {
            // 1er passage (écran prêt) : on adopte la valeur courante sans lancer.
            initRef.current = true;
            seenRef.current = launchReq;
            return;
        }
        if (launchReq !== undefined && launchReq !== seenRef.current) {
            seenRef.current = launchReq;
            useRemoteLaunch.getState().fire(launchReq);
        }
    }, [launchReq, isAnimScreen]);
}

/**
 * Animator Control (Phase 2B) — paramètres du jeu réglés à distance. L'animateur
 * règle (ex. Blind Test : playlist / nb chansons / mode) → le cockpit écrit
 * screenInfos.anim.gameParams. On le lit en live et on pousse les params dans le
 * store useRemoteGameParams ; le bridge de l'iframe (useWebViewPlugin) les relaie
 * au jeu via le message 'set-game-params'. État idempotent : on ne ré-émet vers le
 * store que sur changement réel des valeurs (compare JSON) pour éviter les renvois
 * inutiles à chaque snapshot Firestore.
 */
function useRemoteGameParamsSignal(client: ReturnType<typeof useSyncClient>, device: string, urlGameID: string) {
    const isAnimScreen = !!client && normalizeScreenID(client.getScreenForDevice(device)) === Mode.Anim;
    // Jeu à réglages mémorisés (Blind Test) : ceux de CE jeu, qui survivent au
    // changement de jeu ; sinon `gameParams` comme avant (cf gameParamsMemory).
    const params = isAnimScreen ? savedGameParams(client.getScreen(Mode.Anim), urlGameID) : undefined;
    const lastRef = useRef<string>('');

    useEffect(() => {
        if (!isAnimScreen) return;
        const key = params ? JSON.stringify(params) : '';
        if (key === lastRef.current) return;
        lastRef.current = key;
        useRemoteGameParams.getState().set(params ?? null);
    }, [params, isAnimScreen]);
}

/**
 * TV → compte : enregistre les réglages de partie annoncés par le jeu chargé sur la
 * TV (message `saveGameParams`, Blind Test) pour que la télécommande affiche
 * toujours les mêmes que la TV et qu'ils soient mémorisés pour la soirée suivante.
 * Seulement sur l'écran de bar (mode animé, jamais en aéroport) et pour le jeu
 * réellement chargé. N'écrit rien si le message ne change rien (écho du relais).
 */
function useTvGameParamsSave(client: ReturnType<typeof useSyncClient>, device: string, urlGameID: string) {
    const clientRef = useRef(client);
    clientRef.current = client;
    const lastId = useGameParamsFromGame((s) => s.lastId);
    // Chaque message n'est traité qu'UNE fois (ni au remontage, ni quand l'écran ou
    // le jeu change) : rejouer un vieux « set » écraserait un choix fait depuis au téléphone.
    const doneRef = useRef(useGameParamsFromGame.getState().lastId);

    useEffect(() => {
        const fresh = useGameParamsFromGame.getState().queue.filter((q) => q.id > doneRef.current);
        doneRef.current = fresh.length > 0 ? fresh[fresh.length - 1].id : doneRef.current;
        const c = clientRef.current;
        if (fresh.length === 0 || !c || c.mode === Mode.Auto) return;
        // Démo publique : n'importe quel visiteur y écrit, ses réglages n'iraient
        // qu'aux TV des autres visiteurs.
        if (PUBLIC_DEMO_CLIENTS.has(c.id)) return;
        if (normalizeScreenID(c.getScreenForDevice(device)) !== Mode.Anim) return;
        if (!isMemorizedGame(urlGameID)) return;
        const allowed = new Set(GAME_PARAMS_SCHEMAS[urlGameID]?.fields.map((f) => f.key) ?? []);
        const screen = c.getScreen(Mode.Anim);
        const memory = screen?.gameParamsByGame?.[urlGameID];
        const before = savedGameParams(screen, urlGameID) ?? {};
        let next: Record<string, unknown> | null = null;
        for (const { message } of fresh) {
            if (message.gameID === urlGameID) next = mergeTvParams(next ?? before, message, allowed) ?? next;
        }
        if (!next) return;
        // Réglage par réglage (cf setMemorizedGameParams) ; 1re fois (pas encore de
        // mémoire pour ce jeu) : tout.
        const changes = memory ? changedParams(memory, next) : next;
        if (Object.keys(changes).length === 0) return; // messages qui s'annulent (A → B → A)
        // `gameParams` n'est pas réécrit depuis la TV : il traînerait jusqu'au jeu suivant
        // lancé depuis le menu de la TV (clearScreenGame ne le remet pas à zéro).
        ClientDatabase.setMemorizedGameParams(c.id, Mode.Anim, urlGameID, null, changes).catch(() => {
            /* la TV garde ses réglages ; le prochain geste réessaiera */
        });
    }, [lastId, device, urlGameID]);
}

/**
 * Animator Control (Phase 2D) — stopper la partie à distance (retour lobby).
 * Jumeau de useRemoteLaunchSignal : on lit stopRequest en live, on ignore la valeur
 * présente au montage (anti-arrêt fantôme au reload), on pousse chaque nouvel appui
 * dans le store useRemoteStop ; le bridge relaie 'remote-stop' au jeu.
 */
function useRemoteStopSignal(client: ReturnType<typeof useSyncClient>, device: string) {
    const isAnimScreen = !!client && normalizeScreenID(client.getScreenForDevice(device)) === Mode.Anim;
    const stopReq = isAnimScreen ? client.getScreen(Mode.Anim)?.stopRequest : undefined;

    const seenRef = useRef<number | undefined>(undefined);
    const initRef = useRef(false);

    useEffect(() => {
        if (!isAnimScreen) return;
        if (!initRef.current) {
            initRef.current = true;
            seenRef.current = stopReq;
            return;
        }
        if (stopReq !== undefined && stopReq !== seenRef.current) {
            seenRef.current = stopReq;
            useRemoteStop.getState().fire(stopReq);
        }
    }, [stopReq, isAnimScreen]);
}

/**
 * Animator Control — commande de jeu générique envoyée PENDANT la partie
 * (ex. Unquiz : « question suivante » → `show-next`). Jumeau de
 * useRemoteLaunchSignal : on lit gameCommand.nonce en live, on ignore la valeur
 * présente au montage (anti-commande fantôme au reload), on pousse chaque
 * nouvel appui dans le store useRemoteGameCommand ; le bridge relaie le message
 * `name` au jeu, qui l'exécute s'il le comprend.
 */
function useRemoteGameCommandSignal(client: ReturnType<typeof useSyncClient>, device: string) {
    const isAnimScreen = !!client && normalizeScreenID(client.getScreenForDevice(device)) === Mode.Anim;
    const command = isAnimScreen ? client.getScreen(Mode.Anim)?.gameCommand : undefined;

    const seenRef = useRef<number | undefined>(undefined);
    const initRef = useRef(false);

    useEffect(() => {
        if (!isAnimScreen) return;
        if (!initRef.current) {
            initRef.current = true;
            seenRef.current = command?.nonce;
            return;
        }
        if (command && command.nonce !== seenRef.current) {
            seenRef.current = command.nonce;
            useRemoteGameCommand.getState().fire(command.name, command.nonce);
        }
    }, [command, command?.nonce, isAnimScreen]);
}

export default function ScreenApp() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clientID } = useParams();
    const [searchParams] = useSearchParams();
    const clID = clientID || DEFAULT_CLIENT;
    const gameID = searchParams.get('game') || 'geoloc';
    const device = searchParams.get('device') || 'anim';
    // Double écran « Régie » : cette page est l'écran public (ouverte par la régie).
    // Aucune commande à l'écran ; l'accueil de la soirée et les messages viennent de la régie.
    const publicDisplay = searchParams.get(PUBLIC_DISPLAY_PARAM) === PUBLIC_DISPLAY_VALUE;
    const client = useSyncClient(clID);
    useVenueTitle(clID, client);
    // Écran TV normal ouvert alors que la régie avait laissé l'accueil ou un message
    // (navigateur fermé en pleine soirée double écran) : sa pause figerait ce jeu-ci.
    // On repart d'un état propre, une seule fois. Seuls les bars qui ont utilisé la régie écrivent.
    const regieResetRef = useRef(false);
    const leftoverRegie =
        !!client && client.mode !== Mode.Auto && !publicDisplay && !!client.getScreen(Mode.Anim)?.regie;
    const leftoverView = leftoverRegie ? client?.getScreen(Mode.Anim)?.regie?.view : undefined;
    useEffect(() => {
        if (regieResetRef.current || !client || !leftoverView || leftoverView === 'game') return;
        if (normalizeScreenID(client.getScreenForDevice(device)) !== Mode.Anim) return;
        regieResetRef.current = true;
        ClientDatabase.setRegieView(clID, Mode.Anim, 'game').catch(() => {});
    }, [leftoverView]);
    // Formule Pro : logo + couleurs de l'établissement (services/branding/clientBranding.ts).
    const branding = useClientBranding(clID);
    useBrandTheme(branding);

    // Abonnement en pause : pas de jeu. Retour au menu, qui affiche le calque
    // « Abonnement en pause » (la TV peut arriver ici directement : InitialRedirect,
    // favori, lancement depuis le cockpit juste avant le début de la pause).
    const paused = usePauseActive(client?.pause);
    useEffect(() => {
        if (paused) navigate('/home', { replace: true });
    }, [paused, navigate]);

    useOnboardingFinishedListener(clID, gameID);
    // Les hooks ci-dessous font suivre la clé partagée 'anim' à CET onglet : un
    // autre onglet écran du même bar laissé ouvert en arrière-plan rejouerait
    // chaque jeu en double (son + code de salle à part). Même condition d'écran.
    const screenTab = useScreenTabLock(
        clID,
        // Jamais en aéroport (mode auto), même si l'écran retombe sur 'anim' faute d'instance connue.
        !!client && client.mode !== Mode.Auto && normalizeScreenID(client.getScreenForDevice(device)) === Mode.Anim
    );
    useRemoteGameSwitch(client, device, gameID);
    useRemoteLaunchSignal(client, device);
    useRemoteGameParamsSignal(client, device, gameID);
    useTvGameParamsSave(client, device, gameID);
    useRemoteStopSignal(client, device);
    useRemoteGameCommandSignal(client, device);
    // Nom de l'établissement pour la case newsletter de la manette, qui ne peut
    // pas lire clients/{id}. N'écrit que s'il a changé ; ne jette jamais.
    const clientName = client?.name;
    useEffect(() => {
        syncPublicVenueName(clID, clientName);
    }, [clID, clientName]);
    // Réévalue la présence animateur au fil du temps : sans tick, les boutons
    // opérateur ne réapparaîtraient pas (la présence décroît avec le temps, pas
    // avec un snapshot Firestore — l'appli animateur arrêtée n'écrit plus rien).
    useTick(10_000);
    // Dernière présence animateur connue (ms epoch) — voir plus bas.
    const lastAnimatorSeenRef = useRef<number | undefined>(undefined);

    // makeUnboaredConfig ouvre une connexion socket (`.init()`) : il ne doit
    // s'exécuter qu'UNE fois par page. Appelé dans le corps du render, chaque
    // re-render (useTick 10 s, snapshots useSyncClient) fuyait un socket que
    // UnboaredGame n'utilise pas et que rien ne ferme — les écrans H24 des
    // aéroports accumulaient ainsi des milliers de connexions zombies côté
    // serveur (gotcha #133, bug #11). Ref plutôt que useMemo : la création est
    // garantie unique (useMemo peut être invalidé par React). Un changement
    // ultérieur de mode/serveur n'est pas suivi — comportement inchangé
    // (UnboaredGame ignore déjà l'api après montage) : ces changements passent
    // par un reload de page. La langue, elle, est relayée en direct (plus bas).
    const configRef = useRef<ReturnType<typeof makeUnboaredConfig> | null>(null);
    // Serveur réellement composé par le socket, figé avec lui (le veilleur de lien en dépend).
    const socketServerRef = useRef<string | undefined>(undefined);
    if (client && !configRef.current) {
        socketServerRef.current = client.socketServer;
        configRef.current = makeUnboaredConfig(
            clID,
            client.mode,
            client.language,
            client.socketServer,
            client.getScreenForDevice(device)
        );
    }
    const config = configRef.current;

    // La TV quitte le jeu (retour au menu) : on note la session TELLE QUE le socket l'a
    // ouverte (numéro + lien exact des joueurs), pour que le menu montre le même QR
    // pendant un temps borné (menuSession.ts). Session pas prête → rien.
    useEffect(
        () => () => {
            const api = configRef.current?.api;
            let session: { sessionID: string; link: string } | null = null;
            try {
                if (api) session = { sessionID: api.getSessionID(), link: api.getSessionLink() };
            } catch {
                session = null;
            }
            markLeftGame(clID, session);
            // Retour au menu : le prochain jeu (même identique) est un vrai nouveau lancement.
            try {
                sessionStorage.removeItem('ub_last_game_start');
            } catch {
                /* rien */
            }
        },
        [clID]
    );

    // Mesure du temps mort entre deux parties (GA4, avec `game_end`) : un jeu chargé sur
    // la TV, quel que soit le chemin (menu, télécommande). Bars seulement, hors démos.
    const clientMode = client?.mode;
    useEffect(() => {
        if (!clientMode || clientMode === Mode.Auto || PUBLIC_DEMO_CLIENTS.has(clID)) return;
        if (gameID === ONBOARDING_GAME_ID) return;
        // Un rechargement de la page sur le même jeu n'est pas un nouveau lancement.
        const key = 'ub_last_game_start';
        try {
            const last = JSON.parse(sessionStorage.getItem(key) || 'null') as { g?: string; at?: number } | null;
            if (last?.g === `${clID}:${gameID}` && Date.now() - (last.at ?? 0) < 10 * 60 * 1000) return;
            sessionStorage.setItem(key, JSON.stringify({ g: `${clID}:${gameID}`, at: Date.now() }));
        } catch {
            /* stockage indisponible : on compte quand même */
        }
        logEvent('tv_game_start', clID, { clientID: clID, gameID });
    }, [clID, gameID, clientMode]);

    // Langue de l'établissement suivie en direct. La config ci-dessus est figée au
    // montage : sans ce relais, une langue changée dans Mon compte (clients/{id}.lang)
    // n'atteignait la TV — restée ouverte toute la soirée, pilotée par le cockpit —
    // qu'après un rechargement manuel de la page. setLanguage (local, sans diffusion)
    // déclenche onLanguageChange : i18n de la console (useLanguageManager) et jeu en
    // cours (relais setLanguage de useWebViewPlugin) basculent sans recharger.
    const clientLanguage = client?.language;
    useEffect(() => {
        const api = configRef.current?.api;
        if (!api || !clientLanguage || api.getLanguage() === clientLanguage) return;
        api.setLanguage(clientLanguage);
    }, [clientLanguage]);

    // Spinner « Connexion au serveur… » qui, passé un délai, explique quoi faire
    // (domaine à autoriser, autre réseau, réessayer) au lieu de tourner sans fin.
    // Mémorisé : ScreenApp re-rend toutes les 10 s (useTick) pendant des heures,
    // et l'hôte affiché doit être celui de la config figée (celui que le socket
    // compose vraiment), pas une relecture de client.socketServer.
    const unattended = client?.mode === Mode.Auto;
    const loader = useMemo(
        () =>
            config ? <ScreenConnectLoader serverHost={new URL(config.serverURL).host} unattended={unattended} /> : null,
        [config, unattended]
    );

    if (!client || !config || !loader) {
        return <LoadingPage>{t('home.loadingClient')}</LoadingPage>;
    }

    // Resolve the physical screen so coupon offers can be scoped to it (auto mode).
    // Undefined in anim mode — offers then fall back to client-wide broadcast.
    const screenID = client.getScreenForDevice(device);

    // Animator Control (décision #3) : si un animateur pilote (présence fraîche sur
    // l'écran anim), on masque les boutons opérateur de la TV (mode minimal) ; sinon
    // comportement habituel. Réapparition auto ~30s après l'arrêt de l'appli (plan B).
    //
    // Lissage anti-clignotement : le battement de présence s'écrit avec
    // serverTimestamp() → le snapshot Firestore optimiste repasse animatorSeen à
    // `null` un court instant (~à chaque battement de 15s). Sans garde, les boutons
    // réapparaîtraient brièvement à chaque battement. On retient donc la dernière
    // présence connue ; un vrai départ d'animateur laisse une valeur horodatée
    // (non nulle) qui vieillit normalement au-delà de la fenêtre.
    const animatorSeen =
        normalizeScreenID(screenID) === Mode.Anim ? client.getScreen(Mode.Anim)?.animatorSeen : undefined;
    if (animatorSeen !== undefined) lastAnimatorSeenRef.current = animatorSeen;
    const effectiveAnimatorSeen = animatorSeen ?? lastAnimatorSeenRef.current;
    const animatorPiloting =
        effectiveAnimatorSeen !== undefined && Date.now() - effectiveAnimatorSeen < ANIMATOR_PRESENCE_WINDOW_MS;

    return (
        <div style={{ overflow: 'hidden' }}>
            <GlobalPopup />
            <ScreenTabChoice
                others={screenTab.others}
                onTakeOver={screenTab.takeOver}
                onKeepBoth={screenTab.keepBoth}
            />
            <UnboaredGame loader={loader} {...config}>
                <ScreenSessionRecovery />
                <ScreenStaleLinkReconnect clientID={clID} socketServer={socketServerRef.current} />
                {/* Thème de saison (Halloween) relayé aux téléphones (écran entre deux jeux). */}
                <SeasonalThemeHost clientID={clID} mode={client.mode} />
                {/* Anim/bar screens have no instance heartbeat → publish a presence
                    heartbeat so they appear in the admin live dashboard. Key
                    normalized like the collectors (gotcha #100). */}
                {normalizeScreenID(screenID) === Mode.Anim && <ScreenHeartbeat clientID={clID} />}
                <ScreenManager client={client} />
                {/* Animator Control V1.1 : applique pause / son / veille pilotés à
                    distance (screenInfos.anim) — anim/bar uniquement, hors tuto. */}
                {normalizeScreenID(screenID) === Mode.Anim && gameID !== ONBOARDING_GAME_ID && (
                    <AnimatorScreenControls client={client} screenID={screenID} urlGameID={gameID} />
                )}
                {/* Pendant le tutoriel d'onboarding : pas de boutons système —
                    expérience dédiée, la sortie passe par « Passer le tutoriel »
                    ou le CTA final du jeu (postMessage unboardingFinished). */}
                {gameID !== ONBOARDING_GAME_ID && <BrandCorner branding={branding} />}
                {publicDisplay && normalizeScreenID(screenID) === Mode.Anim && client.mode !== Mode.Auto && (
                    <>
                        <PublicStage client={client} branding={branding} />
                        {/* Sans barre de commandes, il faut garder l'écoute de la touche « QR » de
                            la télécommande (le QR géant s'ouvre par GlobalPopup, hors du bouton). */}
                        <span style={{ display: 'none' }}>
                            <QRCodeButton />
                        </span>
                    </>
                )}
                {gameID !== ONBOARDING_GAME_ID && !publicDisplay && (
                    <ControlPanel
                        preset={client.isDemo() ? Mode.Demo : client.mode}
                        clientID={clID}
                        gameID={gameID}
                        minimal={animatorPiloting}
                    />
                )}
            </UnboaredGame>
        </div>
    );
}
